import { ComponentRef, Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { AppService } from '@app/app.service';
import { ISectionFormField } from '@interfaces/section-form-field';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { cloneDeep } from 'lodash-es';
import { DocumentPreviewModel } from '@app/data-layer/models/documents';
import { QuickFixStepModel } from '../interfaces';
import { IsfModel } from '@app/data-layer/models/isf';
import { enumIsfQuickFixStep } from '../enums';
import { IsfOcrQuickFixSidebarComponent } from '../components';
import { IsfValidationService } from '@services/isf-validation.service';
import { IsfStateService } from './isf-state.service';
import { enumIsfSubStep } from '@app/modules/isf/enums';
import { UtilsHelper } from '@helpers/utils-helper';
import {
  HTS_COUNTRY_OF_ORIGIN_MISSING,
  HTS_DOES_NOT_EXIST,
  HTS_NUMBER_MISSING,
  HTS_NUMBER_WRONG_LENGTH,
} from '@constants/entity-validation-messages';

interface IsfStepStateModel {
  previous: QuickFixStepModel;
  current: QuickFixStepModel;
}

interface IsfStepsStateModel {
  previous: QuickFixStepModel[];
  current: QuickFixStepModel[];
}

interface IsfPreviewModalDocsState {
  previous: DocumentPreviewModel[];
  current: DocumentPreviewModel[];
}

@Injectable({ providedIn: 'root' })
export class IsfOcrQuickFixService {
  public isOpen$: ReplaySubject<boolean> = new ReplaySubject();
  public stepChanged$: ReplaySubject<IsfStepStateModel> = new ReplaySubject();
  public stepsChanged$: ReplaySubject<IsfStepsStateModel> = new ReplaySubject();
  public previewModalDocs$: ReplaySubject<IsfPreviewModalDocsState> = new ReplaySubject();
  public dataSaved$: ReplaySubject<void> = new ReplaySubject();

  private _isf: IsfModel;
  private _isfSteps: QuickFixStepModel[] = [];

  private _isfStep: QuickFixStepModel = {
    manufacturerId: null,
    type: enumIsfQuickFixStep.CONSIGNEE_DETAILS,
  };

  private _isfPreviewModalDocs: DocumentPreviewModel[] = [];

  private sidebarComponentRef: ComponentRef<IsfOcrQuickFixSidebarComponent>;

  private constructor(
    private appService: AppService,
    private isfStateService: IsfStateService,
    private isfValidationService: IsfValidationService,
  ) {}

  public openSidebar(isf: IsfModel): ComponentRef<any> {
    this.isf = isf;
    this.closeSidebar();

    this.isOpen$.next(true);

    this.sidebarComponentRef = this.appService.openSidebar(IsfOcrQuickFixSidebarComponent, {
      sidebarHeaderText: 'BUTTON.QUICK_FIX',
      isMd: true,
      isSidebarHeaderTitleCase: true,
      customZIndex: '1031',
      hideBackDrop: true,
      isEnableScrolling: true,
    });

    this.sidebarComponentRef.instance.isf = this.isf;

    return this.sidebarComponentRef;
  }

  public closeSidebar(): void {
    // this.sidebarComponentRef?.destroy();
    this.appService.closeSidebar();
    this.isOpen$.next(false);
    this.setCurrentStep(null);
  }

  public getSidebarSectionFields(stepType: enumIsfSubStep): ISectionFormField[] {
    let fields: ISectionFormField[] = [];
    switch (stepType) {
      //<-- Consignee Details -->
      case enumIsfSubStep.CONSIGNEE_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.CONSIGNEE',
          },
        ];
        break;
      //<-- Buyer Details -->
      case enumIsfSubStep.BUYER_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.BUYER',
          },
        ];
        break;

      //<-- Ship-To Details -->
      case enumIsfSubStep.SHIP_TO_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.SHIP_TO',
          },
        ];
        break;
      //<-- Supplier Details -->
      case enumIsfSubStep.SUPPLIER_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.SUPPLIER',
          },
        ];
        break;
      //<-- Consolidator Details -->
      case enumIsfSubStep.CONSOLIDATOR_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.CONSOLIDATOR',
          },
        ];
        break;
      //<-- Container Stuffing Location Details -->
      case enumIsfSubStep.CONTAINER_STUFFING_LOCATION_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.STAFFING_LOCATION',
          },
        ];
        break;
      //<-- Manufacturer Details -->
      case enumIsfSubStep.MANUFACTURER_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.MANUFACTURER',
          },
        ];
        break;
      //<-- International HTS Code Details -->
      case enumIsfSubStep.INTERNATIONAL_HTS_CODE_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.INTERNATIONAL_HTS_CODE',
          },
        ];
        break;
      default:
        break;
    }
    return fields;
  }

  public getCurrentStep(): QuickFixStepModel {
    return this.step;
  }

  public setCurrentStep(step: QuickFixStepModel) {
    this.step = step;
  }

  public getCurrentStepsToFix(): QuickFixStepModel[] {
    return this.steps;
  }

  public setCurrentStepsToFix(isf: IsfModel) {
    this.isf = isf;
    const shipment = this.isf.shipment;
    const invoices = this.isf.shipment?.invoices || [];

    const newSteps: QuickFixStepModel[] = [];

    const isQuickFixConsigneeDetailsInvalid = this.isfValidationService.isQuickFixConsigneeDetailsInvalid(shipment);
    const consigneeDetailsErrors = [...(shipment.consignee?.validationMessages || [])];
    const consigneeAddressErrors = shipment.consignee?.address?.validationMessages || {};

    const isAddConsigneeStep =
      consigneeDetailsErrors.length || isQuickFixConsigneeDetailsInvalid || !UtilsHelper.isObjectEmpty(consigneeAddressErrors);

    if (isAddConsigneeStep) {
      newSteps.push({
        type: enumIsfQuickFixStep.CONSIGNEE_DETAILS,
      });
    }

    const isQuickFixBuyerDetailsInvalid = this.isfValidationService.isQuickFixBuyerDetailsInvalid(shipment);
    const buyerDetailsErrors = [...(shipment.buyer?.validationMessages || [])];
    const buyerAddressErrors = shipment.buyer?.address?.validationMessages || {};

    const isAddBuyerStep = buyerDetailsErrors.length || isQuickFixBuyerDetailsInvalid || !UtilsHelper.isObjectEmpty(buyerAddressErrors);

    if (isAddBuyerStep) {
      newSteps.push({
        type: enumIsfQuickFixStep.BUYER_DETAILS,
      });
    }

    const isQuickFixShipToDetailsInvalid = this.isfValidationService.isQuickFixShipToDetailsInvalid(shipment);
    const shipToDetailsErrors = [...(shipment.shipTo?.validationMessages || [])];
    const shipToAddressErrors = shipment.shipTo?.address?.validationMessages || {};

    const isAddShipToStep = shipToDetailsErrors.length || isQuickFixShipToDetailsInvalid || !UtilsHelper.isObjectEmpty(shipToAddressErrors);

    if (isAddShipToStep) {
      newSteps.push({
        type: enumIsfQuickFixStep.SHIP_TO_DETAILS,
      });
    }

    const isQuickFixSupplierDetailsInvalid = this.isfValidationService.isQuickFixSupplierDetailsInvalid(shipment);
    const supplierDetailsErrors = [...(shipment.seller?.validationMessages || [])];
    const supplierAddressErrors = shipment.seller?.address?.validationMessages || {};

    const isAddSupplierStep =
      supplierDetailsErrors.length || isQuickFixSupplierDetailsInvalid || !UtilsHelper.isObjectEmpty(supplierAddressErrors);

    if (isAddSupplierStep) {
      newSteps.push({
        type: enumIsfQuickFixStep.SUPPLIER_DETAILS,
      });
    }

    const isQuickFixConsolidatorDetailsInvalid = this.isfValidationService.isQuickFixConsolidatorDetailsInvalid(shipment);

    const consolidatorDetailsErrors = [...(shipment.consolidator?.validationMessages || [])];
    const consolidatorAddressErrors = shipment.consolidator?.address?.validationMessages || {};

    const isAddConsolidatorStep =
      consolidatorDetailsErrors.length || isQuickFixConsolidatorDetailsInvalid || !UtilsHelper.isObjectEmpty(consolidatorAddressErrors);

    if (isAddConsolidatorStep) {
      newSteps.push({
        type: enumIsfQuickFixStep.CONSOLIDATOR_DETAILS,
      });
    }

    const isQuickFixCslDetailsInvalid = this.isfValidationService.isQuickFixCslDetailsInvalid(shipment);
    const cslDetailsErrors = [...(shipment.stuffingLocation?.validationMessages || [])];
    const cslAddressErrors = shipment.stuffingLocation?.address?.validationMessages || {};

    const isAddCslStep = cslDetailsErrors.length || isQuickFixCslDetailsInvalid || !UtilsHelper.isObjectEmpty(cslAddressErrors);

    if (isAddCslStep) {
      newSteps.push({
        type: enumIsfQuickFixStep.CONTAINER_STUFFING_LOCATION_DETAILS,
      });
    }

    if (!invoices.length) {
      newSteps.push(
        {
          invoiceId: null,
          manufacturerId: null,
          type: enumIsfQuickFixStep.MANUFACTURER_DETAILS,
        },
        {
          invoiceId: null,
          manufacturerId: null,
          type: enumIsfQuickFixStep.INTERNATIONAL_HTS_CODE_DETAILS,
        },
      );
    } else {
      invoices.forEach((invoice) => {
        const isQuickFixManufacturerDetailsInvalid = this.isfValidationService.isQuickFixManufacturerDetailsInvalid(invoice);
        const manufacturerDetailsErrors = invoice?.manufacturer?.validationMessages || [];
        const manufacturerAddressErrors = invoice?.manufacturer?.address?.validationMessages || {};

        const isAddManufacturerStep =
          manufacturerDetailsErrors.length || isQuickFixManufacturerDetailsInvalid || !UtilsHelper.isObjectEmpty(manufacturerAddressErrors);

        if (isAddManufacturerStep) {
          newSteps.push({
            invoiceId: invoice?.id,
            manufacturerId: invoice?.manufacturer?.id,
            type: enumIsfQuickFixStep.MANUFACTURER_DETAILS,
          });
        }

        const isAnyLineItemInvalid = invoice?.lines?.some((lineItem) => {
          const lineItemErrorsToCatch = [HTS_DOES_NOT_EXIST, HTS_NUMBER_MISSING, HTS_NUMBER_WRONG_LENGTH, HTS_COUNTRY_OF_ORIGIN_MISSING];

          const lineItemDetailsErrors = (lineItem?.validationMessages || []).filter((error) => lineItemErrorsToCatch.includes(error.text));

          return lineItemDetailsErrors.length;
        });

        if (!invoice?.lines?.length || isAnyLineItemInvalid) {
          newSteps.push({
            invoiceId: invoice?.id,
            manufacturerId: invoice?.manufacturer?.id,
            type: enumIsfQuickFixStep.INTERNATIONAL_HTS_CODE_DETAILS,
          });
          return;
        }
      });
    }

    const isQuickFixBlDetailsInvalid = this.isfValidationService.isQuickFixBlDetailsInvalid(shipment);

    if (isQuickFixBlDetailsInvalid) {
      newSteps.push({
        type: enumIsfQuickFixStep.BILL_OF_LADING_DETAILS,
      });
    }

    this.steps = newSteps;
  }

  public getSyncedStepsToFix(steps: QuickFixStepModel[]) {
    const isf = this.isf;
    const invoices = isf.shipment?.invoices || [];
    const newSteps: QuickFixStepModel[] = [];

    steps.forEach((step) => {
      const isStepTypeManufacturerRelated = [
        enumIsfQuickFixStep.MANUFACTURER_DETAILS,
        enumIsfQuickFixStep.INTERNATIONAL_HTS_CODE_DETAILS,
      ].includes(step.type);

      if (isStepTypeManufacturerRelated) {
        if (!invoices.length) {
          newSteps.push({
            ...step,
            invoiceId: null,
            manufacturerId: null,
          });
        }

        invoices.forEach((invoice) => {
          newSteps.push({
            ...step,
            invoiceId: invoice?.id,
            manufacturerId: invoice?.manufacturer?.id,
          });
        });
      } else {
        newSteps.push(step);
      }
    });

    return newSteps;
  }

  public getIsf(): IsfModel {
    return this.isf;
  }

  public setIsf(isf: IsfModel) {
    this.isf = isf;
  }

  public getCurrentPreviewModalDocs(): DocumentPreviewModel[] {
    return this.previewModalDocs;
  }

  public setCurrentPreviewModalDocs(previewModalDocs: DocumentPreviewModel[]) {
    this.previewModalDocs = previewModalDocs;
  }

  private get isf(): IsfModel {
    return this._isf;
  }

  private set isf(newIsf: IsfModel) {
    this._isf = newIsf;
    this.isfStateService.currentIsf = newIsf;
  }

  private get step(): QuickFixStepModel {
    return this._isfStep;
  }

  private set step(newStep: QuickFixStepModel) {
    const previousStep = cloneDeep(this.step);
    this._isfStep = newStep;

    const stepState: IsfStepStateModel = {
      previous: previousStep,
      current: newStep,
    };

    this.stepChanged$.next(stepState);
  }

  private get steps(): QuickFixStepModel[] {
    return this._isfSteps;
  }

  private set steps(newSteps: QuickFixStepModel[]) {
    const previousStep = cloneDeep(this.steps);
    this._isfSteps = newSteps;

    const stepsState: IsfStepsStateModel = {
      previous: previousStep,
      current: newSteps,
    };

    this.stepsChanged$.next(stepsState);
  }

  private get previewModalDocs(): DocumentPreviewModel[] {
    return this._isfPreviewModalDocs;
  }

  private set previewModalDocs(newPreviewModalDocs: DocumentPreviewModel[]) {
    const previousPreviewModalDocs = cloneDeep(this.previewModalDocs);
    this._isfPreviewModalDocs = newPreviewModalDocs;

    const previewModalDocsState: IsfPreviewModalDocsState = {
      previous: previousPreviewModalDocs,
      current: newPreviewModalDocs,
    };

    this.previewModalDocs$.next(previewModalDocsState);
  }
}
